<template>
  <div class="w-full min-h-screen flex flex-col">
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <div class="flex-1 w-full">
      <div class="max-w-5xl mx-auto sm:px-6 sm:py-8">
        <!-- Loading -->
        <div
          v-if="loading"
          class="bg-white/90 backdrop-blur-md sm:rounded-2xl shadow-xl border border-gray-100 p-12 flex flex-col items-center justify-center gap-4"
        >
          <img class="h-24" :src="logo" alt="Loading" />
          <p class="text-gray-600">Loading your investigations...</p>
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="bg-white/90 backdrop-blur-md sm:rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
        >
          <p class="text-red-600">{{ error }}</p>
        </div>

        <!-- Content -->
        <div
          v-else
          class="bg-white/90 backdrop-blur-md sm:rounded-2xl shadow-xl border border-gray-100"
        >
          <div class="p-4 sm:p-8 lg:p-12 space-y-6">
            <header class="flex items-center justify-between">
              <h1 class="text-2xl font-semibold">My Uploads</h1>
              <p class="text-sm text-gray-500">
                {{ filteredInvestigations.length }} investigation{{
                  filteredInvestigations.length === 1 ? "" : "s"
                }}
              </p>
            </header>

            <!-- Search -->
            <section class="space-y-2">
              <label for="search" class="sr-only">Search</label>
              <div class="relative">
                <input
                  id="search"
                  v-model.trim="searchQuery"
                  type="text"
                  placeholder="Search by ID or name…"
                  class="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 pr-10 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Search investigations"
                />
                <div
                  class="pointer-events-none absolute inset-y-0 right-3 flex items-center"
                >
                  <Icon icon="mdi:magnify" class="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </section>

            <!-- List -->
            <section class="grid gap-3 sm:grid-cols-1">
              <button
                v-for="inv in pagedInvestigations"
                :key="inv.id"
                type="button"
                @click="selectInvestigation(inv)"
                class="text-left rounded-xl border border-gray-100 bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5"
              >
                <div class="flex items-start justify-between gap-2">
                  <h2
                    class="font-semibold text-periwinkle-500 truncate"
                    :title="inv.title || inv.name"
                  >
                    {{ inv.title || inv.name }}
                  </h2>
                  <span
                    class="shrink-0 text-xs font-medium text-gray-500 bg-white/70 rounded-full px-2 py-0.5"
                  >
                    #{{ inv.id }}
                  </span>
                </div>
                <p class="mt-2 text-sm text-gray-600 flex items-center gap-1">
                  <Icon icon="mdi:account" class="h-4 w-4 text-gray-400" />
                  {{ inv.principal_investigator }}
                </p>
              </button>

              <!-- Empty state -->
              <div
                v-if="filteredInvestigations.length === 0"
                class="col-span-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-8 text-center text-gray-500"
              >
                No investigations match your search.
              </div>
            </section>

            <!-- Pagination -->
            <section>
              <Pagination
                v-if="filteredInvestigations.length > 0"
                :current-page="currentPage"
                :total-pages="totalPages"
                :total-items="filteredInvestigations.length"
                :items-per-page="itemsPerPage"
                label="My Uploads Pagination"
                @first="firstPage"
                @previous="previousPage"
                @page="(page) => (currentPage = page)"
                @next="nextPage"
                @last="lastPage"
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";
import Pagination from "@/components/Pagination.vue";
import apiClient from "@/services/api";

const router = useRouter();
const investigations = ref([]);
const loading = ref(true);
const error = ref(null);
const logo = ref();
const searchQuery = ref("");

onMounted(async () => {
  logo.value = `${process.env.BASE_URL}images/enodo-logo.png`;

  try {
    const response = await apiClient.getMyInvestigationList();

    if (response.data.result === "OK") {
      investigations.value = response.data.message.investigations || [];
    } else {
      console.error(
        "Failed to fetch my investigations:",
        response.data.message,
      );
      error.value =
        response.data.message?.error?.details ||
        "Could not load your investigations";
    }
  } catch (e) {
    console.error("Failed to fetch my investigations:", e);
    error.value = "Could not load your investigations";
  } finally {
    loading.value = false;
  }
});

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

// Newest first (highest id)
const sortedInvestigations = computed(() =>
  [...investigations.value].sort((a, b) => Number(b.id) - Number(a.id)),
);

const filteredInvestigations = computed(() => {
  if (!normalizedQuery.value) return sortedInvestigations.value;

  const q = normalizedQuery.value;
  return sortedInvestigations.value.filter((inv) => {
    const id = String(inv.id);
    const name = String(inv.title || inv.name || "").toLowerCase();
    return id.includes(q) || name.includes(q);
  });
});

/* ---------- Pagination ---------- */
const itemsPerPage = ref(6);
const currentPage = ref(1);

const totalPages = computed(() =>
  Math.max(
    1,
    Math.ceil(filteredInvestigations.value.length / itemsPerPage.value),
  ),
);

watch([filteredInvestigations, itemsPerPage], () => {
  if (currentPage.value > totalPages.value)
    currentPage.value = totalPages.value;
});

watch(searchQuery, () => {
  currentPage.value = 1;
});

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() =>
  Math.min(
    currentPage.value * itemsPerPage.value,
    filteredInvestigations.value.length,
  ),
);

const pagedInvestigations = computed(() =>
  filteredInvestigations.value.slice(startIndex.value, endIndex.value),
);

function firstPage() {
  currentPage.value = 1;
}
function previousPage() {
  if (currentPage.value > 1) currentPage.value--;
}
function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}
function lastPage() {
  currentPage.value = totalPages.value;
}

const selectInvestigation = (inv) => {
  router.push({
    name: "myUploadDataset",
    params: { investigation_id: inv.id },
  });
};
</script>
